import {
  Text,
  TooltipRenderer,
  Box,
  IconHelp,
  Stack,
} from 'braid-design-system';
import type { ComponentProps } from 'react';

import { analyticsEvents } from 'src/constants/analytics';
import { TRACKING_DASHBOARD_JOB_LIST } from 'src/constants/tracking';
import type { DashboardListItemFragment } from 'src/types';
import { getJobAnalyticsUrl } from 'src/utils/getUrls';

import TrackingTextLookingLink from '../../TrackingTextLookingLink/TrackingTextLookingLink';
import { getJobTrackingData } from '../JobItemComponents';

const JobPerformanceLabelAndTooltip = ({
  jobId,
  label,
  textSize = 'small',
  tooltip,
}: {
  label?: string;
  jobId: DashboardListItemFragment['id'];
  textSize?: ComponentProps<typeof Text>['size'];
  tooltip?: string;
}) => {
  if (!label && !tooltip) {
    return null;
  }

  return (
    <Text size={textSize}>
      {label ? label : null}
      {tooltip ? (
        <TooltipRenderer
          id={`jobad-performance-tooltip-${jobId}`}
          tooltip={<Text>{tooltip}</Text>}
          placement="top"
        >
          {({ triggerProps }) => (
            <Box
              display="inline"
              aria-label="Help"
              {...triggerProps}
              paddingLeft="xxsmall"
            >
              <IconHelp
                title="Help"
                titleId={`jobad-performance-help-icon-${jobId}`}
              />
            </Box>
          )}
        </TooltipRenderer>
      ) : null}
    </Text>
  );
};

const JobPerformanceReportLink = ({
  job,
  linkText,
  listPosition,
}: {
  job: DashboardListItemFragment;
  linkText?: string;
  listPosition?: number;
}) => {
  if (!linkText) {
    return null;
  }

  const url = getJobAnalyticsUrl(job.id);

  return (
    <TrackingTextLookingLink
      urlLink={url}
      displayText={linkText}
      textSize="small"
      trackingEventName={analyticsEvents.TRACK_AD_HEALTH}
      trackingActionOrigin={TRACKING_DASHBOARD_JOB_LIST}
      trackingData={getJobTrackingData({ job, listPosition })}
    />
  );
};

export const JobPerformanceView = ({
  job,
  label,
  tooltip,
  linkText,
  textSize,
  listPosition,
}: {
  job: DashboardListItemFragment;
  label?: string;
  tooltip?: string;
  linkText?: string;
  textSize?: ComponentProps<typeof Text>['size'];
  listPosition?: number;
}) => {
  const hasMultipleChildren = (label || tooltip) && linkText;

  return (
    <Box data={{ intercomid: 'jobPerformance' }}>
      <Stack space={hasMultipleChildren ? 'small' : 'none'}>
        <JobPerformanceLabelAndTooltip
          jobId={job.id}
          label={label}
          tooltip={tooltip}
          textSize={textSize}
        />
        <JobPerformanceReportLink
          job={job}
          linkText={linkText}
          listPosition={listPosition}
        />
      </Stack>
    </Box>
  );
};
